import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Resolver, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import MaterialGoodsFields from "../NewMaterialForm/MaterialGoodsFields";
import MaterialCertifications from "../NewMaterialForm/MaterialCertifications";
import MaterialFileUpload from "../NewMaterialForm/MaterialFileUpload";
import ServiceItemNotice from "../NewMaterialForm/ServiceItemNotice";
import { newMaterialServiceValidationSchema } from "../NewMaterialForm/newMaterialFormValidationSchema";
import {
  buildEditMaterialFormValues,
  buildServiceEditBody,
  editMaterialValidationSchema,
  EditMaterialFormValues,
  isServiceMaterial,
  mapCertificationResponsesToRequests,
} from "./editMaterialFormValidationSchema";
import { capitalize, TO_BE_AGREED } from "../../utils";
import i18next from "../../i18n";
import { useFileInputUploader } from "../../hooks";
import { useUpdateMaterial } from "../../hooks/useMaterials";
import { useNotification } from "../../context";
import {
  CertificationRequest,
  MaterialResponse,
  MaterialTagRef,
  SiteResponse,
  TagResponse,
} from "../../types";
import TagPicker from "../carbon/TagPicker/TagPicker";
import FileReader from "../FileReader/FileReader";
import "../NewMaterialForm/newMaterialForm.scss";
import "./editMaterialForm.scss";

export interface EditMaterialFormProps {
  material: MaterialResponse;
  sites: SiteResponse[];
}

// `material.tags` is the lean `MaterialTagRef[]` the DTO sends (id/name/color/description, no
// `active`); `TagPicker` is written against the full `TagResponse` (its `useGetTags` list is the
// full shape). A material's assigned tags are active by definition, and `active` is never
// read in this edit flow (the tags are only displayed and re-submitted as `{id}`), so seeding it
// `true` reconciles the shapes without fabricating anything the form acts on.
const toTagResponses = (refs: MaterialTagRef[] | undefined): TagResponse[] =>
  (refs ?? []).map((tag) => ({ ...tag, active: true }));

/**
 * The edit-material form (`PUT /material/{id}`), rebuilt on the Carbon form primitives (Task 7)
 * in place of 681 lines of MUI/Tailwind + hand-rolled `register` wiring — the same rebuild
 * `NewMaterialForm.tsx` (Task 6) went through, and this form's own template: same RHF+Yup+
 * `Form`/`TextField`/`SelectField` wiring, same `ConfirmDialog` confirm step, same "no field
 * touched beyond RHF's own registered `onChange`" fix for the `measurementUnit`/`deliveryPlace`
 * `<select>`s.
 *
 * This form has no Goods/Services type toggle: a material's kind is fixed by its category. A
 * service shows only its code and name, as on creation (SCRUM-32); see `buildServiceEditBody` for
 * what happens to the fields it hides. A good collects the Goods field set, and that identity is
 * what makes reusing `MaterialGoodsFields`/`MaterialCertifications`/`MaterialFileUpload` (Task 6's own
 * sub-components, imported verbatim, not copied) possible with zero adapter code —
 * `editMaterialFormValidationSchema.ts`'s `EditMaterialFormValues` IS `NewMaterialFormValues`, the
 * exact type those components are already written against. Splitting further than that reuse
 * already buys would just be re-deriving Task 6's own split for no reason; this file stays under
 * the 400-line budget without it.
 *
 * PRELOAD COMPLETENESS (this task's load-bearing requirement — the Sites/Users "comment-wipe"
 * class): every field this form renders AND submits is seeded from `material`, not left to
 * default blank:
 *   - `ean`, `globalMaterialName`, `measurementUnit`, `customMeasurementUnit`, `deliveryFormat`,
 *     `deliveryPlace`, `brand`, `details`, `comments` — RHF fields, seeded via
 *     `buildEditMaterialFormValues(material)` (a pure, unit-tested function — see that module).
 *   - `certifications`, `tags`, `isSpecConfidential`, `materialConfidential` — plain `useState`,
 *     seeded from `material` at initialization AND re-synced by the `useEffect` below (same
 *     defensive precedent `EditUserForm.tsx`/`EditSiteForm.tsx` already establish: `useState`
 *     initializers and `useForm`'s `defaultValues` only ever run on the very first render, so if
 *     `material` arrives or changes after this component has already mounted — this form takes it
 *     as a plain prop and shouldn't assume every caller/test provides the "already loaded before
 *     mount" guarantee `EditMaterial.tsx` currently does — nothing here would otherwise pick that
 *     up).
 *   - `fileURL` — not an RHF field (mirrors Task 6: no Yup rule ever governed it). The record's
 *     existing file, if any, renders via `FileReader` so it's visibly preloaded rather than
 *     silently absent; a freshly picked replacement (`uploadedSpecificationFileUrl`) wins over it
 *     only once the upload actually completes — see `handleConfirm`'s payload.
 *
 * ENDPOINT CONTRACT: `handleConfirm` sends exactly the fields this form owns — `ean`,
 * `globalMaterialName`, `deliveryFormat`, `deliveryPlace`, `measurementUnit`, `comments`, `brand`,
 * `confident`, `confidentSpec`, `fileURL`, `certifications`, `tags`, `details` — never spreading
 * `...material` the way the pre-Carbon `onSubmit` did (which sent `id`/`company`/`active`/
 * `deleted`/`owner`/`category`/`user` along for the ride). The backend already ignores all of
 * those on `PUT /material/{id}`, but this form no longer asserts them either — delegating
 * ownership is the separate Delegate dialog's job, not this form's.
 */
const EditMaterialForm = ({ material, sites }: EditMaterialFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openNotification } = useNotification();
  const isService = isServiceMaterial(material);

  const [isSpecConfidential, setIsSpecConfidential] = useState(
    material?.confidentSpec ?? false
  );
  const [materialConfidential, setMaterialConfidential] = useState(
    material?.confident ?? false
  );
  const [certifications, setCertifications] = useState<CertificationRequest[]>(
    () => mapCertificationResponsesToRequests(material?.certifications)
  );
  const [tags, setTags] = useState<TagResponse[]>(() =>
    toTagResponses(material?.tags)
  );
  const [fileName, setFileName] = useState<string | null>(null);
  const [pendingValues, setPendingValues] =
    useState<EditMaterialFormValues | null>(null);

  const {
    data: uploadedSpecificationFileUrl,
    mutate: uploadFile,
    isPending: isUploading,
  } = useFileInputUploader();

  const defaultValues = useMemo(
    () => buildEditMaterialFormValues(material),
    [material]
  );

  const { control, handleSubmit, reset } = useForm<EditMaterialFormValues>({
    resolver: yupResolver(
      isService
        ? newMaterialServiceValidationSchema
        : editMaterialValidationSchema
    ) as Resolver<EditMaterialFormValues>,
    defaultValues,
  });

  const measurementUnit = useWatch({ control, name: "measurementUnit" });

  // Re-syncs every field this form owns (RHF-managed AND plain `useState`) whenever `material`
  // shows up or changes after mount — see this component's own docstring, PRELOAD COMPLETENESS,
  // for why this can't be left to initializers alone.
  useEffect(() => {
    reset(defaultValues);
    setIsSpecConfidential(material?.confidentSpec ?? false);
    setMaterialConfidential(material?.confident ?? false);
    setCertifications(
      mapCertificationResponsesToRequests(material?.certifications)
    );
    setTags(toTagResponses(material?.tags));
    setFileName(null);
  }, [material, defaultValues, reset]);

  const { mutate: editMaterial, isPending: isUpdatingMaterial } =
    useUpdateMaterial(
      () => {
        openNotification(t("material.success"));
        setPendingValues(null);
        navigate(`/materials/${material.id}`);
      },
      () => {
        setPendingValues(null);
        openNotification(t("newMaterialForm.error"));
      }
    );

  // `Form`'s own `handleSubmit` validates first — only a value that already passed Yup ever opens
  // the confirm dialog, same fix `NewMaterialForm.tsx`/`EditSiteForm.tsx` already apply.
  const onValid = (values: EditMaterialFormValues) => setPendingValues(values);

  const handleConfirm = () => {
    if (!pendingValues || !material) return;

    if (isService) {
      editMaterial({
        materialId: material.id,
        body: buildServiceEditBody(material, pendingValues),
      });
      return;
    }

    const finalMeasurementUnit =
      pendingValues.measurementUnit === i18next.t("measurementUnits.other")
        ? pendingValues.customMeasurementUnit
        : pendingValues.measurementUnit;

    editMaterial({
      materialId: material.id,
      body: {
        ean: pendingValues.ean,
        globalMaterialName: capitalize(pendingValues.globalMaterialName) || "",
        deliveryFormat: pendingValues.deliveryFormat
          ? capitalize(pendingValues.deliveryFormat)
          : "",
        deliveryPlace:
          pendingValues.deliveryPlace === TO_BE_AGREED
            ? null
            : { id: pendingValues.deliveryPlace },
        measurementUnit: finalMeasurementUnit,
        comments: pendingValues.comments
          ? capitalize(pendingValues.comments, true)
          : "",
        brand: pendingValues.brand ? capitalize(pendingValues.brand, true) : "",
        details: pendingValues.details || "",
        confidentSpec: isSpecConfidential,
        confident: materialConfidential,
        fileURL: uploadedSpecificationFileUrl || material.fileURL || "",
        certifications,
        tags: tags.map((tag) => ({ id: tag.id })),
      },
    });
  };

  if (!material) {
    return <Loading withOverlay={false} description={t("cta.loading")} small />;
  }

  return (
    <div className="edit-material-form cds-form-surface">
      <Form
        handleSubmit={handleSubmit}
        onSubmit={onValid}
        noValidate
        className="new-material-form__form"
      >
        {isService && <ServiceItemNotice />}

        <div className="new-material-form__core-grid">
          <TextField
            name="ean"
            label={
              isService
                ? t("newMaterialForm.eanService")
                : t("material.internalCode")
            }
            control={control}
            required
          />
          <TextField
            name="globalMaterialName"
            label={
              isService ? t("newMaterialForm.nameService") : t("material.name")
            }
            control={control}
            required
          />
        </div>

        {!isService && (
          <>
            <MaterialGoodsFields
              control={control}
              measurementUnit={measurementUnit}
              sites={sites}
              materialConfidential={materialConfidential}
              onMaterialConfidentialChange={setMaterialConfidential}
            />

            <div className="new-material-form__tags">
              <TagPicker
                selectedTags={tags}
                onChange={setTags}
                label={t("tags.tags")}
                allowCreate
                maxTags={10}
              />
              <p className="new-material-form__tags-hint">
                {t("tags.tagsHint")}
              </p>
            </div>

            <MaterialCertifications
              certifications={certifications}
              onChange={setCertifications}
            />

            <hr />

            {material.fileURL ? (
              <div className="edit-material-form__current-file">
                <span className="edit-material-form__current-file-label">
                  {t("material.file")}:
                </span>
                <FileReader fileKey={material.fileURL} />
              </div>
            ) : null}

            <MaterialFileUpload
              fileName={fileName}
              isUploading={isUploading}
              onFileSelected={(file) => {
                uploadFile(file);
                setFileName(file.name);
              }}
              onFileRemoved={() => setFileName(null)}
              isSpecConfidential={isSpecConfidential}
              onSpecConfidentialChange={setIsSpecConfidential}
            />
          </>
        )}

        <div className="new-material-form__actions">
          <Button as={Link} to={`/materials/${material.id}`} kind="secondary">
            {t("cta.cancel")}
          </Button>
          <Button type="submit" disabled={isUploading || isUpdatingMaterial}>
            {t("cta.saveChanges")}
          </Button>
        </div>
      </Form>

      <ConfirmDialog
        open={pendingValues !== null}
        title={t("newMaterialForm.edit")}
        body={t("newMaterialForm.editQuestion")}
        confirmLabel={t("cta.edit")}
        onConfirm={handleConfirm}
        onCancel={() => setPendingValues(null)}
        isPending={isUpdatingMaterial}
      />
    </div>
  );
};

export default EditMaterialForm;
